/*
 * Copyright (c) 2022. American Express Company
 */

import H from 'history';
import { LocalDate } from '@js-joda/core';

import MixpanelService from 'src/services/mixpanel.service';
import NavigationService from 'src/services/navigation.service';
import CashFlowService from 'src/services/cashflow.service';
import { InsightElements } from 'src/services/plan.service';
import { StatsModel } from 'src/types/stats.model';
import { BudgetModel } from 'src/types/budget.model';
import { FullUserModel } from 'src/types/user.model';
import { CashFlowModel } from 'src/types/cash-flow.model';
import { BUDGET_OVERALL_ID } from 'src/services/budget.service';

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const processStats = (stats: StatsModel, budgets: BudgetModel[], plan: InsightElements) => {
    return {
        netWorthDifference: stats.netWorth.currentAmount - stats.netWorth.previousAmount,
        savingsDifference: stats.savingsAccounts.currentAmount - stats.savingsAccounts.previousAmount,
        investmentsDifference: stats.investmentAccounts.currentAmount - stats.investmentAccounts.previousAmount,
        savingsPercentDifference: stats.savingsOverview.currentPercent - stats.savingsOverview.currentPercent
    };
};

const addLifeEventOnClick = async (
    history: H.History,
    handleUpdateUserAttribute: (...args: any[]) => any
): Promise<void> => {
    MixpanelService.track('Plan Onboarding', { step: 'doneAddLifeEvent' });
    await handleUpdateUserAttribute('doneAddLifeEvent', true);
    void NavigationService.goTo(history, '/goals', 'Goals Drawer');
};

const viewProjectionsOnClick = async (
    user: FullUserModel,
    history: H.History,
    handleUpdateUserAttribute: (...args: any[]) => any
): Promise<void> => {
    const offset = CashFlowService.getCashFlowIndexWithDate(user.cashFlow, LocalDate.now()) + 1;
    MixpanelService.track('Plan Onboarding', { step: 'doneAddLifeEvent' });
    await handleUpdateUserAttribute('doneViewExpenseProjection', true);
    void NavigationService.goTo(history, `/drawer/show/expenses/${offset}`, 'Show Work Expenses Drawer');
};

const addAccountOnClick = (history: H.History): void => {
    void NavigationService.goTo(history, '/drawer/add-accounts', 'Add Accounts drawer');
};

const viewBudgetsOnClick = (history: H.History): void => {
    void NavigationService.goTo(history, '/budgets');
};

const createBudgetsOnClick = (history: H.History): void => {
    void NavigationService.goTo(history, '/budget/start');
};

const viewBudgetTransactionsOnClick = (
    budget: BudgetModel,
    rawCashFlow: CashFlowModel[],
    handleOpenTransactions: (...a: any[]) => any
): void => {
    const offset: number = CashFlowService.getCashFlowIndexWithDate(rawCashFlow, LocalDate.now());
    const startDate: LocalDate = rawCashFlow[offset].startDate;
    const endDate: LocalDate = rawCashFlow[offset].endDate;
    handleOpenTransactions(
        budget.groupId === BUDGET_OVERALL_ID ? null : budget.groupId,
        startDate,
        endDate,
        'type=TRANSACTION_EXPENSE'
    );
};

const viewRecentTransactionsOnClick = (handleOpenTransactions: (...a: any[]) => any): void => {
    const startDate: LocalDate = LocalDate.now().minusWeeks(1);
    const endDate: LocalDate = LocalDate.now();
    handleOpenTransactions(null, startDate, endDate);
};

const viewReviewTransactionsOnClick = (handleReviewExpenses: (...a: any[]) => any): void => {
    handleReviewExpenses();
};

// TODO how to generate the ID for a new card?
const saveForRetirementOnClick = (history: H.History): void => {
    void NavigationService.goTo(history, '/card/1903', 'Open Card Drawer');
};

export default {
    processStats,
    addLifeEventOnClick,
    viewProjectionsOnClick,
    addAccountOnClick,
    viewBudgetsOnClick,
    viewBudgetTransactionsOnClick,
    viewRecentTransactionsOnClick,
    viewReviewTransactionsOnClick,
    saveForRetirementOnClick,
    createBudgetsOnClick
};
